import { ArrayUtils } from "./tt/arrayutils.js";
import { MathUtils } from "./tt/mathutils.js";
import { TTClient } from "./tt/ttclient.js";
import { TankTrouble } from "./tanktrouble.js";
import emojis from "@asger-finding/discord_emoji";
import { Assets } from "./assets.js";
import { AudioManager } from "./audiomanager.js";
import { Backend } from "./backend.js";
import { Caches } from "./caches.js";
import { ChatAutocomplete } from "./chatautocomplete.js";
import { ClientManager } from "./clientmanager.js";
import { FocusManager } from "./focusmanager.js";
import { GameSession } from "./gamesession.js";
import { Images } from "./images.js";
import { OverlayManager } from "./overlaymanager.js";
import { ResizeManager } from "./resizemanager.js";
import { UIConstants } from "./tt/ui/uiconstants.js";
import { Users } from "./users.js";
import { Utils } from "./utils.js";

export class ChatBox {
    static EVENTS = {
        CHAT_ACTIVITY: "chat activity",
        GLOBAL_CHAT: "global chat",
        CHAT: "chat",
        USER_CHAT: "user chat",
        REPORT_CHAT: "report chat",
        UNDO_CHAT_REPORT: "undo chat report"
    };

    // jQuery objects.
    static chat = null;
    static chatContent = null;
    static chatForm = null;
    static chatBody = null;
    static chatBodyResizeHandle = null;
    static chatInput = null;
    static chatStatus = null;
    static chatStatusNew = null;
    static chatScrollbar = null;
    static chatScrollbarThumb = null;
    static chatWhistle = null;

    // Other elements.
    static chatSendAudio = null;
    static chatNotificationAudio = null;
    static mentionAutocomplete = null;
    static emojiAutocomplete = null;

    // State.
    static updateChatTimeout = null;
    static resizeHandleTimeout = null;
    static scrollSuppressionTimeout = null;
    static scrollSuppressed = false;
    static scrollbarDragOffset = null;
    static whistleChatMessageId = null;
    static globalMessage = false;
    static ignoredPlayerIds = [];
    static ignoreeUsernames = [];
    static addingToIgnoreList = false;
    static recipientPlayerIds = [];
    static recipientUsernames = [];
    static lastPrivateSenderPlayerIds = null;
    static messages = [];

    // Events.
    static eventListeners = [];

    static init() {
        ChatBox.chat = $("<div id='chat'></div>");
        ChatBox.chatStatus = $(`<img class='status button' ${Images.attributes("chat/chatDisabled.png", "Chat")}/>`);
        ChatBox.chatStatusNew = $(`<img class='status button' ${Images.attributes("chat/newMessageMarker.png")}/>`);
        ChatBox.chatContent = $("<div class='content'></div>");
        ChatBox.chatForm = $("<form/>");
        ChatBox.chatInput = $("<textarea disabled rows='1' placeholder='Signal lost' maxlength='255'/>");

        ChatBox.chatBody = $("<div class='body'></div>");
        ChatBox.chatBodyResizeHandle = $(`<img class='ui-resizable-handle ui-resizable-se handle' ${Images.attributes("chat/resizeHandleBottomRight.png")}/>`);
        ChatBox.chatScrollbar = $("<div class='scrollbar'></div>");
        ChatBox.chatScrollbarThumb = $("<div class='thumb'></div>");
        ChatBox.chatWhistle = $(`<img class='whistle' ${Images.attributes("chat/report.png")} title=''/>`);

        ChatBox.chatForm.append(ChatBox.chatInput);
        ChatBox.chatBody.append(ChatBox.chatBodyResizeHandle);
        ChatBox.chatScrollbar.append(ChatBox.chatScrollbarThumb);
        ChatBox.chatContent.append(ChatBox.chatForm, ChatBox.chatBody);
        ChatBox.chat.append(ChatBox.chatStatus, ChatBox.chatStatusNew, ChatBox.chatContent, ChatBox.chatScrollbar, ChatBox.chatWhistle);
        $("body").append(ChatBox.chat);

        ChatBox.chatStatusNew.add(ChatBox.chatBody).add(ChatBox.chatScrollbar).add(ChatBox.chatWhistle).hide();

        ChatBox.chatStatus.add(ChatBox.chatStatusNew).on("click", event => {
            if ($(event.currentTarget).hasClass("noclick")) {
                $(event.currentTarget).removeClass("noclick");
            } else {
                ChatBox.toggle();
            }
        });

        ChatBox.chat.draggable({
            scroll: false,
            cancel: "input, textarea, button, select, option, .body, .scrollbar",
            start: () => {
                ChatBox.chatBody.addClass("dragging");
                ChatBox.chatStatus.add(ChatBox.chatStatusNew).addClass("noclick");
                ChatBox._showResizeHandle();
            },
            drag: (event, ui) => {
                ui.position.left = Math.max($("#content").offset().left + 34, Math.min(ui.position.left, $("#content").offset().left + $("#content").width() - 30 - ChatBox.chatBody.width()));
                ui.position.top = Math.max(20, Math.min(ui.position.top, $(window).height() - 40));
            },
            stop: () => {
                setTimeout(() => {
                    ChatBox.chatStatus.add(ChatBox.chatStatusNew).removeClass("noclick");
                }, 1);
                ChatBox.chatBody.removeClass("dragging");
                ChatBox._hideResizeHandleLater();
            }
        });

        // Initial chat position.
        ChatBox.chat.css("left", $("#content").offset().left + 34);
        ChatBox.chat.css("top", 20);

        ChatBox.chatSendAudio = new Audio(Assets.url("audio/ChatSend.m4a"));
        ChatBox.chatNotificationAudio = new Audio(Assets.url("audio/ChatNotification.m4a"));

        ChatBox._initInput();
        ChatBox._initBody();
        ChatBox._initScrollbar();
        ChatBox._initResizeHandle();
        ChatBox._initWhistle();
        ChatBox._initAutocomplete();

        // Add event listeners.
        ResizeManager.addEventListener(ChatBox._resizeEventHandler, ChatBox);
        Users.addEventListener(ChatBox._authenticationEventHandler, ChatBox);
        ClientManager.getClient().addEventListener(ChatBox._clientEventHandler, ChatBox);
        ClientManager.getClient().addStateChangeListener(ChatBox._clientStateHandler, ChatBox);
    }

    static addEventListener(callback, context) {
        ChatBox.eventListeners.push({ callback, context });
    }

    static removeEventListener(callback, context) {
        for (let i = 0; i < ChatBox.eventListeners.length; i++) {
            if (ChatBox.eventListeners[i].callback === callback && ChatBox.eventListeners[i].context === context) {
                // Remove single entry from array, and return immediately
                // as continuing iteration is unsafe, as the underlying array
                // has been modified
                ChatBox.eventListeners.splice(i, 1);
                return;
            }
        }
    }

    static toggle() {
        if (!ChatBox._isOpenOrOpening()) {
            ChatBox.open();
        } else {
            ChatBox.close();
        }
    }

    static open() {
        ChatBox.focus();

        if (!ChatBox._isOpenOrOpening()) {
            ChatBox.chat.addClass("opening");

            ChatBox.chatBody.stop(true).delay(300).fadeIn(200, () => {
                ChatBox.chat.removeClass("opening").addClass("open");
            });
            ChatBox.chatStatusNew.hide("scale", 50);

            // Wait until chat body has started fading in to render messages.
            ChatBox.updateChatTimeout = setTimeout(() => {
                ChatBox._refreshChat();
            }, 300);
        }
    }

    static close() {
        if (ChatBox._isOpenOrOpening()) {
            ChatBox.chat.removeClass("open opening");
            ChatBox.blur();

            ChatBox.chatBody.stop(true).fadeOut(200);

            clearTimeout(ChatBox.updateChatTimeout);
            ChatBox._hideResizeHandle();
            ChatBox._updateWhistles();
            ChatBox._hideAutocompletes();
        }
    }

    static focus() {
        if (ChatBox.chatInput.prop("disabled")) {
            return;
        }

        ChatBox.chatInput.trigger("focus");
    }

    static blur() {
        ChatBox.chatInput.trigger("blur");
    }

    static addRecipient(playerId) {
        if (ChatBox.chatInput.prop("disabled")) {
            return;
        }

        // Get username from player details.
        Backend.getPlayerDetails(playerId, Caches.getPlayerDetailsCache())
            .then(result => {
                if (typeof result === "object") {
                    if (!ChatBox.recipientUsernames.includes(result.getUsername())) {
                        ChatBox.chatInput.val(`@${result.getUsername()} ${ChatBox.chatInput.val()}`);
                        ChatBox._parseChat();
                        ChatBox._updateInputHeight();
                    }
                    ChatBox.focus();
                } else {
                    ChatBox.addSystemMessage([], "Could not find user");
                }
            });
    }

    static addSystemMessage(playerIds, message, unignorable) {
        ChatBox._lookUpUsernames(playerIds).then(usernameMap => {
            ChatBox._addSystemMessage(playerIds, usernameMap, message, unignorable);
        });
    }

    static addGlobalChatMessage(from, message, chatMessageId) {
        ChatBox._lookUpUsernamesAndAddChatMessage(from, null, false, "#68c5ff", "#333333", message, chatMessageId);
    }

    static addChatMessage(from, message, chatMessageId) {
        ChatBox._lookUpUsernamesAndAddChatMessage(from, null, false, "#000000", "#ffffff", message, chatMessageId);
    }

    static addUserChatMessage(from, to, message, chatMessageId) {
        const addRecipients = from.some(playerId => Users.isAnyUser(playerId));

        ChatBox._lookUpUsernamesAndAddChatMessage(from, to, addRecipients, "#00ff02", "#333333", message, chatMessageId);
    }

    static sendChat() {
        if (ChatBox.chatInput.prop("disabled")) {
            return;
        }

        const message = ChatBox._parseChat();

        // Look up player ids from usernames.
        if (ChatBox.ignoreeUsernames.length > 0) {
            ChatBox._lookUpPlayerIdsByUsernames(ChatBox.ignoreeUsernames).then(({ playerIds, adminPlayerIds, localPlayerIds }) => {
                const newlyChangedIgnoreePlayerIds = [];
                for (const playerId of playerIds) {
                    if (ChatBox.addingToIgnoreList) {
                        if (!ChatBox.ignoredPlayerIds.includes(playerId)) {
                            ChatBox.ignoredPlayerIds.push(playerId);
                            newlyChangedIgnoreePlayerIds.push(playerId);
                        }
                    } else if (ChatBox.ignoredPlayerIds.includes(playerId)) {
                        ChatBox.ignoredPlayerIds.splice(ChatBox.ignoredPlayerIds.indexOf(playerId), 1);
                        newlyChangedIgnoreePlayerIds.push(playerId);
                    }
                }

                // Let players know they cannot ignore admins.
                if (adminPlayerIds.length > 0) {
                    ChatBox.addSystemMessage(adminPlayerIds, "You cannot ignore admins ( @  )");
                }

                // Mock the players ignoring themselves.
                if (ChatBox.addingToIgnoreList && localPlayerIds.length > 0) {
                    ChatBox.addSystemMessage(localPlayerIds, "It's not healthy to ignore [yourself,|yourselves,] @ ");
                }

                if (newlyChangedIgnoreePlayerIds.length > 0) {
                    if (ChatBox.addingToIgnoreList) {
                        ChatBox._removeMentions(newlyChangedIgnoreePlayerIds);
                        ChatBox.addSystemMessage(newlyChangedIgnoreePlayerIds, "You are now ignoring @ ", true);
                    } else {
                        ChatBox.addSystemMessage(newlyChangedIgnoreePlayerIds, "You can now hear @  again");
                    }
                }

                ChatBox._clearInput();
                ChatBox.blur();
            });
        } else if (ChatBox.recipientUsernames.length > 0) {
            ChatBox._lookUpPlayerIdsByUsernames(ChatBox.recipientUsernames).then(({ playerIds, adminPlayerIds, localPlayerIds }) => {
                for (const playerId of [...playerIds, ...adminPlayerIds]) {
                    if (!ChatBox.recipientPlayerIds.includes(playerId)) {
                        ChatBox.recipientPlayerIds.push(playerId);
                    }
                }

                // Mock the players whispering to themselves.
                if (localPlayerIds.length > 0) {
                    ChatBox.addSystemMessage(localPlayerIds, "Why are you talking to [yourself,|yourselves,] @  ?");
                }

                // Only send the message if any non-local players are left.
                if (ChatBox.recipientPlayerIds.length > 0) {
                    ChatBox._sendChat(message);
                } else {
                    ChatBox._clearInput();
                    ChatBox.blur();
                }
            });
        } else {
            ChatBox._sendChat(message);
        }
    }

    static _isOpenOrOpening() {
        return ChatBox.chat.hasClass("open") || ChatBox.chat.hasClass("opening");
    }

    static _notifyEventListeners(evt, data) {
        for (const eventListener of ChatBox.eventListeners) {
            eventListener.callback(eventListener.context, evt, data);
        }
    }

    static _initInput() {
        ChatBox.chatInput.on("keydown", event => {
            ChatBox._inputKeyDownHandler(event);
        });

        ChatBox.chatInput.on("focus", () => {
            ChatBox._notifyEventListeners(ChatBox.EVENTS.CHAT_ACTIVITY);
            GameSession.disableGameInput();
        });

        ChatBox.chatInput.on("blur", () => {
            GameSession.enableGameInput();
        });

        ChatBox.chatInput.on("input", event => {
            ChatBox._notifyEventListeners(ChatBox.EVENTS.CHAT_ACTIVITY);
            if (!event.originalEvent?.isComposing) {
                const [inputElement] = ChatBox.chatInput;
                const wordAtCaretEnd = ChatBox._getWordAtCaret()?.end ?? inputElement.selectionEnd;
                const textUntilWordAtCaretEnd = inputElement.value.slice(0, wordAtCaretEnd);
                const translatedText = ChatBox._translateEmojiShortcodes(textUntilWordAtCaretEnd);
                if (translatedText !== textUntilWordAtCaretEnd) {
                    inputElement.setRangeText(translatedText, 0, wordAtCaretEnd, "end");
                }
            }
            ChatBox._parseChat();
            ChatBox._updateInputHeight();
            if (!event.originalEvent?.isComposing) {
                ChatBox._updateAutocomplete();
            }
        });
    }

    static _inputKeyDownHandler(event) {
        const autocompleteShowing = ChatBox.mentionAutocomplete.isShowing() || ChatBox.emojiAutocomplete.isShowing();

        switch (event.key) {
            case "Enter":
            case "Tab":
            {
                if (autocompleteShowing && ChatBox._submitAutocomplete(ChatBox, event)) {
                    return;
                }
                if (event.key === "Enter") {
                    event.preventDefault();
                    event.stopPropagation();
                    ChatBox.sendChat();
                }
                break;
            }
            case "ArrowUp":
            case "ArrowDown":
            {
                if (autocompleteShowing) {
                    const highlightShift = event.key === "ArrowUp" ? -1 : 1;
                    ChatBox.mentionAutocomplete.navigate(highlightShift);
                    ChatBox.emojiAutocomplete.navigate(highlightShift);
                    event.preventDefault();
                }
                break;
            }
            case "Escape":
            {
                event.stopPropagation();
                ChatBox._hideAutocompletes();
                ChatBox.blur();
                break;
            }
        }
    }

    static _sendChat(message) {
        if (message === "") {
            ChatBox.blur();
            return;
        }

        const translatedMessage = ChatBox._translateEmojiShortcodes(message);

        // Play audio cue.
        AudioManager.playSound(ChatBox.chatSendAudio);
        ChatBox.chat.addClass("send");
        ChatBox._updateInputBackground(true);
        ChatBox.blur();
        ChatBox.chatInput.prop("disabled", true);
        if (ChatBox.globalMessage) {
            ChatBox._notifyEventListeners(ChatBox.EVENTS.GLOBAL_CHAT, translatedMessage);
        } else if (ChatBox.recipientPlayerIds.length > 0) {
            ChatBox._notifyEventListeners(ChatBox.EVENTS.USER_CHAT, { recipientPlayerIds: ChatBox.recipientPlayerIds, message: translatedMessage });
        } else {
            ChatBox._notifyEventListeners(ChatBox.EVENTS.CHAT, translatedMessage);
        }
    }

    static _translateEmojiShortcodes(text) {
        return text.replaceAll(/:(?<name>[\p{L}\p{N}_]+):/gu, (shortcode, name) => Object.hasOwn(emojis, name) ? emojis[name] : shortcode);
    }

    static _parseChat() {
        // Clear ignoree usernames.
        ChatBox.ignoreeUsernames = [];
        // Clear recipient player ids and usernames.
        ChatBox.recipientPlayerIds = [];
        ChatBox.recipientUsernames = [];
        ChatBox.globalMessage = false;

        ChatBox.chat.removeClass("global user");

        const message = ChatBox.chatInput.val().trim();
        // First check if global message.
        if (message.charAt(0) === "#") {
            ChatBox.globalMessage = true;

            ChatBox.chat.addClass("global");

            return message.slice(1);
        } else if (message.slice(0, 2) === "/r" && ChatBox.lastPrivateSenderPlayerIds) {
            // Reply to last private message.
            ChatBox.recipientPlayerIds = ChatBox.lastPrivateSenderPlayerIds;

            ChatBox.chat.addClass("user");

            return message.slice(2);
        } else if ((message.slice(0, 2) === "/i" || message.slice(0, 2) === "/u") && message.length > 2) {
            // Add or remove players to ignore list.
            ChatBox.addingToIgnoreList = message.slice(0, 2) === "/i";
            for (const ignoree of message.slice(2).split(" ")) {
                if (ignoree !== "" && !ChatBox.ignoreeUsernames.includes(ignoree)) {
                    ChatBox.ignoreeUsernames.push(ignoree);
                }
            }

            return "";
        } else {
            // Try to parse recipient tokens.
            const tokens = message.split(" ");

            let i = 0;
            for (; i < tokens.length; ++i) {
                const firstCharacter = tokens[i].charAt(0);
                if (firstCharacter === "@") {
                    const recipient = tokens[i].slice(1);
                    if (recipient !== "" && !ChatBox.recipientUsernames.includes(recipient)) {
                        ChatBox.recipientUsernames.push(recipient);
                    }
                } else if (firstCharacter !== "") {
                    break;
                }
            }
            if (ChatBox.recipientPlayerIds.length > 0 || ChatBox.recipientUsernames.length > 0) {
                ChatBox.chat.addClass("user");
            }

            return tokens.slice(i).join(" ");
        }
    }

    static _removeLocalPlayersFromIgnored() {
        const removedLocalPlayerIds = [];

        ChatBox.ignoredPlayerIds = ChatBox.ignoredPlayerIds.filter(
            item => {
                const localPlayer = Users.getAllPlayerIds().includes(item);
                if (localPlayer) {
                    removedLocalPlayerIds.push(item);
                }
                return !localPlayer;
            }
        );

        if (removedLocalPlayerIds.length > 0) {
            ChatBox.addSystemMessage(removedLocalPlayerIds, "You cannot ignore [yourself,|yourselves,] @ ");
        }
    }

    static _clearInput() {
        ChatBox.chatInput.val("");
        ChatBox.chatInput.outerHeight(16);
        ChatBox._parseChat();
        ChatBox._hideAutocompletes();
    }

    static _disableInput(placeholder) {
        ChatBox.chatInput.attr("placeholder", placeholder);
        ChatBox.chatInput.prop("disabled", true);
        ChatBox._clearInput();
    }

    static _updateInputHeight() {
        ChatBox.chatInput.outerHeight(16).outerHeight(ChatBox.chatInput[0].scrollHeight);
    }

    static _updateInputBackground(animate) {
        const sending = ChatBox.chat.hasClass("send");
        if (!animate) {
            ChatBox.chatForm.prop("stripeValue", sending ? 100 : 0);
            ChatBox.chatForm.removeAttr("style");
            return;
        }

        let backgroundColour = "#ffffff";
        let stripeColour = "#cccccc";
        let valueScale = 1;
        if (ChatBox.chat.hasClass("global")) {
            backgroundColour = "#68c5ff";
            stripeColour = "#ffffff";
            valueScale = sending ? 1 : -1;
        } else if (ChatBox.chat.hasClass("user")) {
            backgroundColour = "#00ff02";
            stripeColour = "#ffffff";
            valueScale = sending ? 1 : -1;
        }

        ChatBox.chatForm.stop(true).delay(sending ? 500 : 0).animate({ stripeValue: sending ? 100 : 0 }, { duration: 100, step: value => {
            const stripeOffset = (1 - (value / 100)) * 10 * valueScale;
            ChatBox.chatForm.css("background", `repeating-linear-gradient(53deg, ${stripeColour} 0, ${stripeColour} ${10 - stripeOffset}px, ${backgroundColour} ${10 - stripeOffset}px, ${backgroundColour} ${30 + stripeOffset}px, ${stripeColour} ${30 + stripeOffset}px, ${stripeColour} 40px`);
        }, complete: () => {
            if (!sending) {
                ChatBox.chatForm.removeAttr("style");
            }
        } });
    }

    static _initAutocomplete() {
        ChatBox.mentionAutocomplete = new ChatAutocomplete(ChatBox.chatInput, "mention", ChatBox._submitAutocomplete, ChatBox);
        ChatBox.emojiAutocomplete = new ChatAutocomplete(ChatBox.chatInput, "emoji", ChatBox._submitAutocomplete, ChatBox);

        for (const [name, emoji] of Object.entries(emojis)) {
            ChatBox.emojiAutocomplete.addOption(`:${name}:`, emoji, `:${name}:`, Infinity);
        }
    }

    static _hideAutocompletes() {
        ChatBox.mentionAutocomplete.hide();
        ChatBox.emojiAutocomplete.hide();
    }

    static _getWordAtCaret() {
        const [inputElement] = ChatBox.chatInput;
        let start = 0;
        for (const word of inputElement.value.split(/\s/u)) {
            const end = start + word.length;
            if (start < inputElement.selectionEnd && end >= inputElement.selectionEnd) {
                return { word, start, end };
            }
            start = end + 1;
        }
        return null;
    }

    static _isMentionWord(wordAtCaret) {
        const [leadingMentions] = ChatBox.chatInput.val().split(/\s+(?=[^@])/u);
        return wordAtCaret !== null && leadingMentions.startsWith("@") && wordAtCaret.end <= leadingMentions.length;
    }

    static _isEmojiWord(wordAtCaret) {
        return wordAtCaret !== null && wordAtCaret.word.startsWith(":") && !wordAtCaret.word.endsWith(":") && wordAtCaret.word.length >= 3;
    }

    static _updateAutocomplete() {
        const wordAtCaret = ChatBox._getWordAtCaret();

        for (const [autocomplete, focused, labelOffset] of [
            [ChatBox.mentionAutocomplete, ChatBox._isMentionWord(wordAtCaret), 1],
            [ChatBox.emojiAutocomplete, ChatBox._isEmojiWord(wordAtCaret), 0]
        ]) {
            if (!focused) {
                autocomplete.hide();
                continue;
            }

            autocomplete.setSearchTerm(wordAtCaret.word);
            if (autocomplete.matches.length > 0) {
                autocomplete.show();
                autocomplete.updatePosition(wordAtCaret.start + labelOffset, wordAtCaret.end);
            } else {
                autocomplete.hide();
            }
        }
    }

    static _submitAutocomplete(self, event) {
        const wordAtCaret = ChatBox._getWordAtCaret();
        let match = null;
        if (ChatBox._isMentionWord(wordAtCaret)) {
            match = ChatBox.mentionAutocomplete.getHighlightedMatch();
        } else if (ChatBox._isEmojiWord(wordAtCaret)) {
            match = ChatBox.emojiAutocomplete.getHighlightedMatch();
        }
        if (match === null) {
            return false;
        }

        const [inputElement] = ChatBox.chatInput;
        const textBeforeCaret = `${inputElement.value.slice(0, wordAtCaret.start)}${match}`;
        const textAfterCaret = inputElement.value.slice(wordAtCaret.end);
        const separator = textAfterCaret.startsWith(" ") ? "" : " ";
        inputElement.value = `${textBeforeCaret}${separator}${textAfterCaret}`;
        inputElement.setSelectionRange(textBeforeCaret.length + 1, textBeforeCaret.length + 1);

        event.preventDefault();
        event.stopPropagation();
        inputElement.dispatchEvent(new InputEvent("input"));
        ChatBox.focus();

        return true;
    }

    static _addMentions(playerIds) {
        for (const playerId of playerIds) {
            if (Users.isAnyUser(playerId)) {
                continue;
            }

            Backend.getPlayerDetails(playerId, Caches.getPlayerDetailsCache())
                .then(result => {
                    if (typeof result === "object" && Utils.maskUnapprovedUsername(result) === result.getUsername() && !ChatBox.ignoredPlayerIds.includes(playerId)) {
                        ChatBox.mentionAutocomplete.addOption(`@${result.getUsername()}`, "", result.getUsername(), 10 * 60 * 1000);
                    }
                })
                .catch(() => {});
        }
    }

    static _removeMentions(playerIds) {
        for (const playerId of playerIds) {
            Backend.getPlayerDetails(playerId, Caches.getPlayerDetailsCache())
                .then(result => {
                    if (typeof result === "object") {
                        ChatBox.mentionAutocomplete.removeOption(`@${result.getUsername()}`);
                    }
                })
                .catch(() => {});
        }
    }

    static _lookUpPlayerIdsByUsernames(usernames) {
        const detailsPromises = usernames.map(username => {
            return Backend.getPlayerDetailsByUsername(username, Caches.getPlayerDetailsByUsernameCache())
                .then(result => {
                    if (typeof result === "object") {
                        return result;
                    }

                    ChatBox.addSystemMessage([], `Could not find user ${result}`);
                    return null;
                })
                .catch(() => null);
        });

        return Promise.all(detailsPromises).then(results => {
            const playerIds = [];
            const adminPlayerIds = [];
            const localPlayerIds = [];
            for (const result of results) {
                if (result === null) {
                    continue;
                }

                if (Users.isAnyUser(result.getPlayerId())) {
                    localPlayerIds.push(result.getPlayerId());
                } else if (result.getGmLevel() === null) {
                    playerIds.push(result.getPlayerId());
                } else {
                    adminPlayerIds.push(result.getPlayerId());
                }
            }

            return { playerIds, adminPlayerIds, localPlayerIds };
        });
    }

    static _lookUpUsernames(playerIds) {
        const usernameMap = {};
        const detailsPromises = playerIds.map(playerId => {
            usernameMap[playerId] = "<ERROR>";
            return Backend.getPlayerDetails(playerId, Caches.getPlayerDetailsCache())
                .then(result => {
                    if (typeof result === "object") {
                        usernameMap[result.getPlayerId()] = Utils.maskUnapprovedUsername(result);
                    }
                })
                .catch(() => {});
        });

        return Promise.all(detailsPromises).then(() => usernameMap);
    }

    static _lookUpUsernamesAndAddChatMessage(from, to, addRecipients, textColour, strokeColour, message, chatMessageId) {
        ChatBox._lookUpUsernames(addRecipients ? [...from, ...to] : from).then(usernameMap => {
            ChatBox._addChatMessage(from, to, usernameMap, addRecipients, textColour, strokeColour, message, chatMessageId);
        });
    }

    static _addSystemMessage(involvedPlayerIds, involvedUsernameMap, message, unignorable) {
        // Ignore messages from ignored players.
        if (!unignorable && ArrayUtils.containsSome(involvedPlayerIds, ChatBox.ignoredPlayerIds)) {
            return;
        }

        const systemMessage = { type: "system", involvedPlayerIds, involvedUsernameMap, message, localPlayerIds: Users.getAllPlayerIds() };
        ChatBox._storeMessage(systemMessage);

        if (ChatBox.chat.hasClass("open")) {
            ChatBox._renderSystemMessage(systemMessage, true);
        }

        ChatBox._addMentions(involvedPlayerIds);
    }

    static _addChatMessage(from, to, usernameMap, addRecipients, textColour, strokeColour, message, chatMessageId) {
        // Ignore messages from ignored players.
        if (ArrayUtils.containsSome(from, ChatBox.ignoredPlayerIds)) {
            return;
        }

        const chatMessage = { type: "chat", from, to, usernameMap, addRecipients, textColour, strokeColour, message, chatMessageId, reported: false, localPlayerIds: Users.getAllPlayerIds() };
        ChatBox._storeMessage(chatMessage);

        if (ChatBox.chat.hasClass("open")) {
            ChatBox._renderChatMessage(chatMessage, true);
        }

        ChatBox._addMentions(to === null ? from : [...from, ...to]);

        // If user message, also show exclamation mark.
        ChatBox._notifyNewMessage(Array.isArray(to) && ArrayUtils.containsSome(to, Users.getAllPlayerIds()));
    }

    static _storeMessage(message) {
        ChatBox.messages.push(message);

        if (ChatBox.messages.length > UIConstants.CHAT_BOX_MAX_MESSAGE_COUNT) {
            const droppedMessage = ChatBox.messages.shift();
            ChatBox.chatBody.children(".chatMessage").last().remove();
            ChatBox._getReportedWhistle(droppedMessage.chatMessageId).remove();
        }
    }

    static _findChatMessage(chatMessageId) {
        return ChatBox.messages.find(message => message.type === "chat" && message.chatMessageId === chatMessageId);
    }

    static _getSenderUsernames(chatMessage) {
        return Utils.arrayToNaturalString(chatMessage.from.filter(playerId => !Users.isAnyUser(playerId)).map(playerId => chatMessage.usernameMap[playerId]));
    }

    static _createUsernameElement(playerId, username, suffix, localPlayerIds) {
        const usernameElement = $("<span class='username'></span>");
        usernameElement.attr("data-player-id", playerId);
        usernameElement.text(`${username}${suffix}`);
        if (localPlayerIds.includes(playerId)) {
            return usernameElement;
        }

        usernameElement.addClass("foreign");
        usernameElement.on("click", event => {
            if (Users.getHighestGmLevel() >= UIConstants.ADMIN_LEVEL_PLAYER_LOOKUP && event.shiftKey) {
                OverlayManager.pushOverlay(TankTrouble.AdminPlayerLookupOverlay, {
                    adminId: Users.getHighestGmUser(),
                    playerId
                });
                return;
            }
            if (!ChatBox.chatInput.prop("disabled")) {
                ChatBox.addRecipient(playerId);
            }
        });

        usernameElement.tooltipster({ content: `Whisper to ${username}`, side: "bottom", distance: 12 });

        return usernameElement;
    }

    static _renderSystemMessage(systemMessage, animate) {
        const { involvedPlayerIds, involvedUsernameMap, message, localPlayerIds } = systemMessage;
        const systemMessageElement = $("<div class='chatMessage'></div>");

        let pendingText = "";
        const appendPendingText = () => {
            if (pendingText !== "") {
                systemMessageElement.append($("<span class='systemText'></span>").text(pendingText));
                pendingText = "";
            }
        };

        for (const word of message.split(" ")) {
            if (word === "") {
                continue;
            }

            if (word === "§") {
                appendPendingText();
                systemMessageElement.append("<br>");
            } else if (word === "@" && involvedPlayerIds.length > 0) {
                for (let i = 0; i < involvedPlayerIds.length; ++i) {
                    const playerId = involvedPlayerIds[i];
                    appendPendingText();
                    systemMessageElement.append(ChatBox._createUsernameElement(playerId, involvedUsernameMap[playerId], "", localPlayerIds));

                    if (i === involvedPlayerIds.length - 2) {
                        pendingText += " and ";
                    } else if (i < involvedPlayerIds.length - 2) {
                        pendingText += ", ";
                    } else {
                        pendingText += " ";
                    }
                }
            } else if (word.charAt(0) === "[" && word.includes("|")) {
                const pluralityVariations = word.split("|");
                const variation = involvedPlayerIds.length === 1 ? pluralityVariations[0].slice(1) : pluralityVariations[1].slice(0, -1);
                pendingText += `${variation} `;
            } else {
                pendingText += `${word} `;
            }
        }
        appendPendingText();

        ChatBox._insertMessageElement(systemMessageElement, animate);

        ChatBox._updatePlayerDetails(involvedPlayerIds);
    }

    static _renderChatMessage(chatMessage, animate) {
        const { from, to, usernameMap, addRecipients, textColour, strokeColour, message, chatMessageId, reported, localPlayerIds } = chatMessage;
        const chatMessageElement = $("<div class='chatMessage'></div>");
        chatMessageElement.attr("data-chat-message-id", chatMessageId);
        const messageContent = $("<span class='messageContent'></span>");

        for (let i = 0; i < from.length; ++i) {
            let suffix = ":";
            if (i < from.length - 1) {
                suffix = ",";
            } else if (addRecipients) {
                suffix = " @";
            }
            messageContent.append(ChatBox._createUsernameElement(from[i], usernameMap[from[i]], suffix, localPlayerIds), " ");
        }

        if (addRecipients) {
            for (let i = 0; i < to.length; ++i) {
                const suffix = i < to.length - 1 ? "," : ":";
                messageContent.append(ChatBox._createUsernameElement(to[i], usernameMap[to[i]], suffix, localPlayerIds), " ");
            }
        }

        let lastIndex = 0;
        for (const urlMatch of message.matchAll(/https?:\/\/[\w\-_]+(?:\.[\w\-_]+)+(?:[\w\-.,@?^=%&:/~+#]*[\w\-@?^=%&/~+#])?/gu)) {
            if (urlMatch.index > lastIndex) {
                messageContent.append($("<span class='messageText'></span>").text(message.slice(lastIndex, urlMatch.index)));
            }
            messageContent.append($("<a class='messageText' target='_blank' rel='noopener'></a>").attr("href", urlMatch[0]).text(urlMatch[0]));
            lastIndex = urlMatch.index + urlMatch[0].length;
        }
        if (lastIndex < message.length) {
            messageContent.append($("<span class='messageText'></span>").text(message.slice(lastIndex)));
        }
        messageContent.children(".messageText").css({ color: textColour, "--stroke-colour": strokeColour });

        chatMessageElement.append(messageContent);

        chatMessageElement.toggleClass("reportable", from.some(playerId => !localPlayerIds.includes(playerId)));

        ChatBox._insertMessageElement(chatMessageElement, animate);

        ChatBox._updatePlayerDetails(addRecipients ? [...from, ...to] : from);

        if (reported) {
            ChatBox._addReportedWhistle(chatMessage);
        }
    }

    static _insertMessageElement(messageElement, animate) {
        ChatBox.chatBody.prepend(messageElement);
        if (animate) {
            messageElement.css({ display: "none", opacity: 0 }).show(200).animate({ opacity: 1 }, 200);
        }
    }

    static _refreshChat() {
        ChatBox.chatBody.children(".chatMessage").remove();
        ChatBox.chat.children(".reportedWhistle").remove();

        for (const message of ChatBox.messages) {
            if (message.type === "chat") {
                ChatBox._renderChatMessage(message, false);
            } else {
                ChatBox._renderSystemMessage(message, false);
            }
        }

        ChatBox.chatBody.scrollTop(0);
        ChatBox._updateScrollbar();
    }

    static _notifyNewMessage(showNewStatus) {
        if (FocusManager.isFocused()) {
            // Shake status icon if closed.
            if (!ChatBox.chat.hasClass("open")) {
                const statusElements = [ChatBox.chatStatus.show("scale", 1)];
                if (showNewStatus) {
                    statusElements.push(ChatBox.chatStatusNew.show("scale", 50));
                }
                for (const statusElement of statusElements) {
                    for (let i = 0; i < 2; ++i) {
                        statusElement
                            .animate({ height: "+=10px", width: "+=10px", top: "-=5px", left: "-=5px" }, 125)
                            .animate({ height: "-=10px", width: "-=10px", top: "+=5px", left: "+=5px" }, 125);
                    }
                }
            }
        } else {
            // Play audio cue.
            AudioManager.playSound(ChatBox.chatNotificationAudio);

            // Show status icon if closed.
            if (!ChatBox.chat.hasClass("open") && showNewStatus) {
                ChatBox.chatStatusNew.show("scale", 50);
            }
        }
    }

    static _updatePlayerDetails(playerIds) {
        for (const playerId of playerIds) {
            Backend.getPlayerDetails(playerId, Caches.getPlayerDetailsCache())
                .then(result => {
                    if (typeof result === "object") {
                        const baseColour = `#${result.getBaseColour().numericValue.slice(2).padStart(6, "0")}`;

                        // Check if colour is so bright that we want a dark stroke.
                        const baseRed = parseInt(baseColour.slice(1, 3), 16);
                        const baseGreen = parseInt(baseColour.slice(3, 5), 16);
                        const baseBlue = parseInt(baseColour.slice(5, 7), 16);

                        const brightness = (0.299 * baseRed) + (0.587 * baseGreen) + (0.114 * baseBlue);

                        const strokeColour = brightness >= 145.0 ? "#333333" : "#ffffff";

                        ChatBox.chatBody.find(`.username[data-player-id='${result.getPlayerId()}']`)
                            .css({ color: baseColour, "--stroke-colour": strokeColour });
                    }
                });
        }
    }

    static _initBody() {
        new ResizeObserver(() => {
            const bodyWidth = ChatBox.chatBody[0].offsetWidth;
            if (bodyWidth > 0) {
                ChatBox.chat[0].style.setProperty("--chat-body-width", `${bodyWidth}px`);
                ChatBox.chat[0].style.setProperty("--chat-body-height", `${ChatBox.chatBody[0].clientHeight}px`);
            }
            ChatBox._updateResizeHandlePosition();
            ChatBox._updateScrollFade();
            ChatBox._updateScrollbar();
            ChatBox._updateWhistles();
            if (ChatBox._isOpenOrOpening()) {
                ChatBox._updateInputHeight();
            }
        }).observe(ChatBox.chatBody[0]);

        new MutationObserver(() => {
            ChatBox._updateScrollFade();
            ChatBox._updateScrollbar();
            ChatBox._updateWhistles();
        }).observe(ChatBox.chatBody[0], { childList: true, subtree: true, attributes: true, attributeFilter: ["style"] });

        ChatBox.chatBody.on("scroll", () => {
            ChatBox._updateScrollFade();
            ChatBox._updateScrollbar();
            ChatBox._hideWhistle(false);
            ChatBox._updateWhistles();

            ChatBox.scrollSuppressed = true;
            clearTimeout(ChatBox.scrollSuppressionTimeout);
            ChatBox.scrollSuppressionTimeout = setTimeout(() => {
                ChatBox.scrollSuppressed = false;
                ChatBox._showWhistleForHoveredMessage();
            }, 150);
        });

        ChatBox.chatBody.on("mousedown", event => {
            if (event.button !== 0) {
                return;
            }

            $("body").addClass("chatSelecting");
            document.addEventListener("mouseup", () => {
                $("body").removeClass("chatSelecting");
            }, { once: true });
        });
    }

    static _updateScrollFade() {
        const [chatBodyElement] = ChatBox.chatBody;
        const { scrollTop, scrollHeight, clientHeight } = chatBodyElement;
        ChatBox.chat[0].style.setProperty("--chat-scroll-top", `${scrollTop}px`);
        ChatBox.chat[0].style.setProperty("--chat-scroll-bottom", `${Math.max(0, scrollHeight - clientHeight - scrollTop)}px`);
    }

    static _initScrollbar() {
        ChatBox.chatScrollbarThumb.on("pointerdown", event => {
            ChatBox.scrollbarDragOffset = event.clientY - event.currentTarget.getBoundingClientRect().top;
            event.currentTarget.setPointerCapture(event.pointerId);
            ChatBox.chatScrollbarThumb.addClass("dragging");
            event.preventDefault();
        });

        ChatBox.chatScrollbarThumb.on("pointermove", event => {
            if (ChatBox.scrollbarDragOffset === null) {
                return;
            }

            const scrollbarRectangle = ChatBox.chatScrollbar[0].getBoundingClientRect();
            const trackHeight = scrollbarRectangle.height - ChatBox.chatScrollbarThumb.outerHeight();
            if (trackHeight <= 0) {
                return;
            }

            const thumbTop = Math.max(0, Math.min(event.clientY - scrollbarRectangle.top - ChatBox.scrollbarDragOffset, trackHeight));
            const [chatBodyElement] = ChatBox.chatBody;
            chatBodyElement.scrollTop = (thumbTop / trackHeight) * (chatBodyElement.scrollHeight - chatBodyElement.clientHeight);
        });

        ChatBox.chatScrollbarThumb.on("pointerup pointercancel", event => {
            ChatBox.scrollbarDragOffset = null;
            event.currentTarget.releasePointerCapture(event.pointerId);
            ChatBox.chatScrollbarThumb.removeClass("dragging");
        });
    }

    static _updateScrollbar() {
        const [chatBodyElement] = ChatBox.chatBody;
        const { scrollTop, scrollHeight, clientHeight } = chatBodyElement;
        if (!ChatBox._isOpenOrOpening() || scrollHeight <= clientHeight) {
            ChatBox.chatScrollbar.hide();
            return;
        }
        ChatBox.chatScrollbar.show();

        if (ChatBox.scrollbarDragOffset === null) {
            ChatBox.chatScrollbar.css({ left: chatBodyElement.offsetLeft - 12, top: chatBodyElement.offsetTop, height: clientHeight });
        }

        const thumbHeight = MathUtils.linearInterpolation(20, 50, clientHeight / scrollHeight);
        const thumbTop = (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - thumbHeight);
        ChatBox.chatScrollbarThumb.css({ height: thumbHeight, top: thumbTop });
    }

    static _initResizeHandle() {
        ChatBox.chatBody.resizable({
            ghost: true,
            handles: { se: ChatBox.chatBodyResizeHandle },
            distance: 0,
            minWidth: 180,
            minHeight: 100,
            maxHeight: 500,
            start: (event, ui) => {
                ui.helper.find(".chatMessage").remove();
                ui.helper.css("border-radius", "3px");
                ui.helper.css("border", "2px dotted #aaaaaa");
                ui.helper.css("left", "-=2px");
                ui.helper.css("top", "-=2px");
                ui.originalElement.resizable("option", "maxWidth", $("#content").offset().left + $("#content").width() - 10 - ui.position.left);
                ChatBox._hideResizeHandle();
            },
            stop: () => {
                ChatBox._showResizeHandle();
                ChatBox._hideResizeHandleLater();
            }
        });

        ChatBox.chat.append(ChatBox.chatBodyResizeHandle);
        ChatBox.chatBodyResizeHandle.hide();
    }

    static _showResizeHandle() {
        clearTimeout(ChatBox.resizeHandleTimeout);
        if (!ChatBox._isOpenOrOpening()) {
            return;
        }

        ChatBox.chatBodyResizeHandle.stop(true).css({ display: "", opacity: 0 });
        ChatBox._updateResizeHandlePosition();
        ChatBox.chatBodyResizeHandle.animate({ opacity: 1 }, 200);
    }

    static _hideResizeHandle() {
        clearTimeout(ChatBox.resizeHandleTimeout);
        ChatBox.chatBodyResizeHandle.stop(true).hide();
    }

    static _hideResizeHandleLater() {
        clearTimeout(ChatBox.resizeHandleTimeout);
        ChatBox.resizeHandleTimeout = setTimeout(() => {
            ChatBox.chatBodyResizeHandle.stop(true).fadeOut(200);
        }, 5000);
    }

    static _updateResizeHandlePosition() {
        const [resizeHandleElement] = ChatBox.chatBodyResizeHandle;
        const [chatBodyElement] = ChatBox.chatBody;
        if (resizeHandleElement.offsetWidth === 0) {
            return;
        }

        resizeHandleElement.style.left = `${chatBodyElement.offsetLeft + chatBodyElement.offsetWidth - resizeHandleElement.offsetWidth}px`;
        resizeHandleElement.style.top = `${chatBodyElement.offsetTop + chatBodyElement.offsetHeight - resizeHandleElement.offsetHeight}px`;
    }

    static _initWhistle() {
        ChatBox.chatWhistle.tooltipster({ side: "right" });

        ChatBox.chatBody.on("mouseenter", ".chatMessage.reportable", event => {
            if (!ChatBox.scrollSuppressed) {
                ChatBox._showWhistle($(event.currentTarget));
            }
        });

        ChatBox.chatBody.on("mouseleave", ".chatMessage.reportable", event => {
            if (!ChatBox.chatWhistle.is(event.relatedTarget)) {
                ChatBox._hideWhistle(true);
            }
        });

        ChatBox.chatWhistle.on("mouseleave", event => {
            if ($(event.relatedTarget).closest(".chatMessage.reportable").length === 0) {
                ChatBox._hideWhistle(true);
            }
        });

        ChatBox.chatWhistle.on("click", () => {
            if (ChatBox.whistleChatMessageId !== null) {
                ChatBox._notifyEventListeners(ChatBox.EVENTS.REPORT_CHAT, ChatBox.whistleChatMessageId);
            }
        });
    }

    static _showWhistle(chatMessageElement) {
        const chatMessage = ChatBox._findChatMessage(Number(chatMessageElement.attr("data-chat-message-id")));
        const position = ChatBox._getWhistlePosition(chatMessageElement[0]);
        if (!chatMessage || chatMessage.reported || position === null) {
            ChatBox._hideWhistle(false);
            return;
        }

        const whistleWasShowing = ChatBox.whistleChatMessageId !== null;
        ChatBox.whistleChatMessageId = chatMessage.chatMessageId;

        ChatBox.chatWhistle.stop(true).css(position);
        if (whistleWasShowing) {
            ChatBox.chatWhistle.css("display", "block").animate({ opacity: 1 }, 300);
        } else {
            ChatBox.chatWhistle.css({ display: "block", opacity: 0 }).animate({ opacity: 1 }, 200);
        }

        ChatBox.chatWhistle.tooltipster("content", `Report ${ChatBox._getSenderUsernames(chatMessage)}`);
        ChatBox.chatWhistle.tooltipster("reposition");
    }

    static _hideWhistle(animate) {
        ChatBox.whistleChatMessageId = null;
        ChatBox.chatWhistle.tooltipster("close");
        ChatBox.chatWhistle.stop(true);
        if (animate) {
            ChatBox.chatWhistle.fadeOut(200);
        } else {
            ChatBox.chatWhistle.hide();
        }
    }

    static _showWhistleForHoveredMessage() {
        const hoveredChatMessageElement = ChatBox.chatBody.children(".chatMessage.reportable").filter((index, element) => element.matches(":hover"));
        if (hoveredChatMessageElement.length > 0) {
            ChatBox._showWhistle(hoveredChatMessageElement);
        }
    }

    static _getChatMessageElement(chatMessageId) {
        return ChatBox.chatBody.children(`.chatMessage[data-chat-message-id='${chatMessageId}']`);
    }

    static _getReportedWhistle(chatMessageId) {
        return ChatBox.chat.children(`.reportedWhistle[data-chat-message-id='${chatMessageId}']`);
    }

    static _getWhistlePosition(chatMessageElement) {
        if (!chatMessageElement || !ChatBox._isOpenOrOpening()) {
            return null;
        }

        const messageContent = chatMessageElement.querySelector(".messageContent") ?? chatMessageElement;
        const lastPartRange = document.createRange();
        lastPartRange.selectNodeContents(messageContent.lastElementChild ?? messageContent);
        const lastLineRectangle = [...lastPartRange.getClientRects()].at(-1);
        if (!lastLineRectangle) {
            return null;
        }

        const bodyRectangle = ChatBox.chatBody[0].getBoundingClientRect();
        if (bodyRectangle.height === 0 || lastLineRectangle.bottom < bodyRectangle.top || lastLineRectangle.top > bodyRectangle.bottom) {
            return null;
        }

        const chatRectangle = ChatBox.chat[0].getBoundingClientRect();
        return {
            left: lastLineRectangle.right - chatRectangle.left + 2,
            top: lastLineRectangle.top - chatRectangle.top - 10,
            "--chat-whistle-top": `${lastLineRectangle.top - bodyRectangle.top - 10}px`
        };
    }

    static _updateWhistles() {
        if (ChatBox.whistleChatMessageId !== null) {
            const position = ChatBox._getWhistlePosition(ChatBox._getChatMessageElement(ChatBox.whistleChatMessageId)[0]);
            if (position === null) {
                ChatBox._hideWhistle(false);
            } else {
                ChatBox.chatWhistle.css(position);
                ChatBox.chatWhistle.tooltipster("reposition");
            }
        }

        ChatBox.chat.children(".reportedWhistle").each((index, element) => {
            ChatBox._updateReportedWhistle($(element));
        });
    }

    static _addReportedWhistle(chatMessage) {
        const reportedWhistle = $(`<div class='reportedWhistle'><img ${Images.attributes("chat/reportSelected.png")}/></div>`);
        reportedWhistle.attr("data-chat-message-id", chatMessage.chatMessageId);
        reportedWhistle.tooltipster({ content: `You have reported ${ChatBox._getSenderUsernames(chatMessage)}`, side: "right" });
        reportedWhistle.on("click", () => {
            ChatBox._notifyEventListeners(ChatBox.EVENTS.UNDO_CHAT_REPORT, chatMessage.chatMessageId);
        });

        ChatBox.chat.append(reportedWhistle);
        ChatBox._updateReportedWhistle(reportedWhistle);
    }

    static _updateReportedWhistle(reportedWhistle) {
        const position = ChatBox._getWhistlePosition(ChatBox._getChatMessageElement(reportedWhistle.attr("data-chat-message-id"))[0]);
        if (position === null) {
            reportedWhistle.tooltipster("close");
            reportedWhistle.hide();
            return;
        }

        reportedWhistle.css({ display: "", ...position });
        reportedWhistle.tooltipster("reposition");
    }

    static _setChatMessageReported(chatMessageId, reported) {
        const chatMessage = ChatBox._findChatMessage(chatMessageId);
        if (chatMessage) {
            chatMessage.reported = reported;
        }

        const chatMessageElement = ChatBox._getChatMessageElement(chatMessageId);
        const reportedWhistle = ChatBox._getReportedWhistle(chatMessageId);

        if (reported) {
            if (chatMessage && chatMessageElement.length > 0 && reportedWhistle.length === 0) {
                ChatBox._addReportedWhistle(chatMessage);
            }
            ChatBox._hideWhistle(false);
        } else {
            reportedWhistle.remove();
            ChatBox._showWhistleForHoveredMessage();
        }
    }

    static _clientEventHandler(self, evt, data) {
        switch (evt) {
            case TTClient.EVENTS.GLOBAL_CHAT_POSTED:
            {
                self._handleChatSendReceipt(data.getSendReceipt());
                self.addGlobalChatMessage(data.getFrom(), data.getMessage(), data.getChatMessageId());

                break;
            }
            case TTClient.EVENTS.CHAT_POSTED:
            {
                self._handleChatSendReceipt(data.getSendReceipt());
                self.addChatMessage(data.getFrom(), data.getMessage(), data.getChatMessageId());

                break;
            }
            case TTClient.EVENTS.USER_CHAT_POSTED:
            {
                self._handleChatSendReceipt(data.getSendReceipt());
                self.addUserChatMessage(data.getFrom(), data.getTo(), data.getMessage(), data.getChatMessageId());

                // Store latest private message sender if it came from someone else.
                if (!data.getFrom().some(playerId => Users.isAnyUser(playerId))) {
                    self.lastPrivateSenderPlayerIds = data.getFrom();
                }

                break;
            }
            case TTClient.EVENTS.SYSTEM_CHAT_POSTED:
            {
                self._handleChatSendReceipt(data.getSendReceipt());
                self.addSystemMessage(data.getInvolvedPlayerIds(), data.getMessage());

                break;
            }
            case TTClient.EVENTS.PLAYERS_BANNED:
            {
                self.addSystemMessage(data, "@  [was|were] temporarily banned");

                break;
            }
            case TTClient.EVENTS.PLAYERS_UNBANNED:
            {
                self.addSystemMessage(data, "@  [was|were] unbanned");

                break;
            }
            case TTClient.EVENTS.CHAT_REPORTED:
            {
                self._setChatMessageReported(data, true);

                break;
            }
            case TTClient.EVENTS.CHAT_REPORT_UNDONE:
            {
                self._setChatMessageReported(data, false);

                break;
            }
            case TTClient.EVENTS.PLAYERS_AUTHENTICATED:
            {
                self._updateAvailability();

                break;
            }
            case TTClient.EVENTS.GAME_LIST_CHANGED:
            {
                self._addMentions(ClientManager.getClient().getAvailableGameStates().flatMap(gameState => gameState.getPlayerStates().map(playerState => playerState.getPlayerId())));

                break;
            }
        }
    }

    static _handleChatSendReceipt(sendReceipt) {
        if (sendReceipt === "") {
            return;
        }

        ChatBox.chat.removeClass("send");
        ChatBox._updateInputBackground(true);

        ChatBox._clearInput();
        ChatBox._updateAvailability();
    }

    static _updateAvailability() {
        const verifiedPlayerIds = [];
        const detailsPromises = Users.getAuthenticatedPlayerIds().map(playerId => {
            return Backend.getPlayerDetails(playerId, Caches.getPlayerDetailsCache())
                .then(result => {
                    if (typeof result === "object" && result.getVerified()) {
                        verifiedPlayerIds.push(playerId);
                    }
                })
                .catch(() => {});
        });

        Promise.all(detailsPromises).then(() => {
            if (Users.getAllPlayerIds().length === 0) {
                ChatBox._disableInput("Join to transmit");
            } else if (verifiedPlayerIds.length > 0) {
                const mpAuthenticatedPlayerIds = ClientManager.getClient().getAuthenticatedPlayerIds();
                // Only enable input if there are any verified players also authenticated on the mp server.
                if (mpAuthenticatedPlayerIds.length > 0 && ArrayUtils.containsSome(mpAuthenticatedPlayerIds, verifiedPlayerIds)) {
                    ChatBox.chatInput.attr("placeholder", "Message");
                    ChatBox.chatInput.prop("disabled", ChatBox.chat.hasClass("send"));
                } else {
                    ChatBox._disableInput("Please wait");
                }
            } else if (Users.getGuestPlayerIds().length > 0) {
                ChatBox._disableInput("Sign up to transmit");
            } else {
                ChatBox._disableInput("Verify email to transmit");
            }
        });
    }

    static _addChatRequirementMessage(playerIds, requirement) {
        if (Users.getAllPlayerIds().length === 1) {
            ChatBox.addSystemMessage(playerIds, `You must ${requirement} to chat`);
        } else {
            ChatBox.addSystemMessage(playerIds, `@  must ${requirement} to chat`);
        }
    }

    static _clientStateHandler(self, oldState, newState) {
        switch (newState) {
            case TTClient.STATES.UNCONNECTED:
            {
                self.chatStatus.attr("src", Images.url("chat/chatDisabled.png"));
                self.chatStatusNew.hide("scale", 50);

                self.chat.removeClass("send");
                self._updateInputBackground(false);

                self._disableInput("Signal lost");

                self.mentionAutocomplete.clearOptions();

                break;
            }
            case TTClient.STATES.HANDSHAKED:
            {
                if (oldState === TTClient.STATES.CONNECTED) {
                    self.chatStatus.attr("src", Images.url("chat/chat.png"));
                    const server = ClientManager.getAvailableServers()[ClientManager.getMultiplayerServerId()];
                    const connectedMessage = server ? `Connected to ${server.name}` : "Connected";
                    self.addSystemMessage([], connectedMessage);
                    self._updateAvailability();
                }

                break;
            }
        }
    }

    static _authenticationEventHandler(self, evt, data) {
        self._removeLocalPlayersFromIgnored();

        switch (evt) {
            case Users.EVENTS.GUEST_ADDED:
            {
                self._addChatRequirementMessage([data], "sign up and verify email");
                break;
            }
            case Users.EVENTS.GUESTS_ADDED:
            {
                self._addChatRequirementMessage(data, "sign up and verify email");
                break;
            }
            case Users.EVENTS.GUEST_SIGNED_UP:
            case Users.EVENTS.PLAYER_SIGNED_UP:
            {
                self._addChatRequirementMessage([data], "verify email");
                break;
            }
        }

        self._updateAvailability();
    }

    static _resizeEventHandler(self, evt) {
        switch (evt) {
            case ResizeManager.EVENTS.REFRESH:
            case ResizeManager.EVENTS.RESIZE:
            {
                self.chat.position({
                    my: "left top",
                    at: `left+${Math.min(
                        Math.max(
                            self.chat.position().left,
                            $("#content").offset().left + 34
                        ),
                        $("#content").offset().left + $("#content").width() - self.chatBody.width() - 30
                    )} top+${Math.min(self.chat.position().top, $(window).height() - 40)}`,
                    of: $(window)
                });

                break;
            }
        }
    }
}

TankTrouble.ChatBox = ChatBox;
